import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Fingerprint, Trash2, Plus, ShieldCheck } from "lucide-react";
import { enrollPasskey, listPasskeys, deletePasskey, passkeySupported } from "../../lib/passkey";
import { formatApiError } from "../../lib/api";

function fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return "";
    }
}

export default function SecuritySettings() {
    const [passkeys, setPasskeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [supported, setSupported] = useState(true);

    const load = useCallback(async () => {
        try {
            const list = await listPasskeys();
            setPasskeys(list);
        } catch {
            // ignore — empty state will render
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setSupported(passkeySupported());
        load();
    }, [load]);

    const handleAdd = async () => {
        setAdding(true);
        try {
            const label =
                typeof navigator !== "undefined" && /iPhone|iPad|Macintosh/.test(navigator.userAgent)
                    ? "Apple device"
                    : /Android/.test(navigator.userAgent || "")
                    ? "Android device"
                    : "This device";
            await enrollPasskey(label);
            toast.success("Passkey added — you can now sign in with biometrics.");
            await load();
        } catch (err) {
            if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
                // user cancelled — stay silent
            } else {
                toast.error(formatApiError(err) || "Couldn't add a passkey on this device.");
            }
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deletePasskey(id);
            setPasskeys((p) => p.filter((k) => k.id !== id));
            toast.success("Passkey removed");
        } catch (err) {
            toast.error(formatApiError(err) || "Couldn't remove that passkey.");
        }
    };

    return (
        <div data-testid="settings-security" className="space-y-4">
            <section className="bg-snowcap border border-ridge-line rounded-lg p-6">
                <div className="flex items-start gap-3 mb-5">
                    <div className="w-10 h-10 rounded-md bg-midnight-pine/10 flex items-center justify-center shrink-0">
                        <Fingerprint className="w-5 h-5 text-ember-orange" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="font-display font-bold text-2xl text-midnight-pine mb-1">Passkeys</h2>
                        <p className="text-sm text-slate-gray">
                            Sign in with Face ID, Touch ID, or your fingerprint instead of a password.
                            Passkeys are tied to this device and can't be phished.
                        </p>
                    </div>
                </div>

                {!supported && (
                    <div
                        data-testid="settings-security-unsupported"
                        className="px-4 py-3 rounded-md bg-alpine-mist border border-ridge-line text-sm text-slate-gray mb-4"
                    >
                        This browser or device doesn't support passkeys. Try a modern browser on a device with
                        biometric unlock.
                    </div>
                )}

                {loading ? (
                    <div className="text-sm text-slate-gray">Loading…</div>
                ) : passkeys.length === 0 ? (
                    <div
                        data-testid="settings-security-empty"
                        className="px-4 py-6 rounded-md border border-dashed border-ridge-line text-center mb-4"
                    >
                        <ShieldCheck className="w-8 h-8 text-slate-gray mx-auto mb-2" />
                        <p className="text-sm text-slate-gray">You haven't added any passkeys yet.</p>
                    </div>
                ) : (
                    <ul data-testid="settings-security-list" className="space-y-2 mb-4">
                        {passkeys.map((pk) => (
                            <li
                                key={pk.id}
                                data-testid={`passkey-row-${pk.id}`}
                                className="flex items-center gap-3 px-4 py-3 rounded-md border border-ridge-line bg-white"
                            >
                                <Fingerprint className="w-5 h-5 text-midnight-pine shrink-0" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-bold text-granite truncate">{pk.name}</div>
                                    <div className="text-[11px] text-slate-gray">
                                        Added {fmtDate(pk.created_at)}
                                        {pk.last_used ? ` · Last used ${fmtDate(pk.last_used)}` : ""}
                                    </div>
                                </div>
                                <button
                                    data-testid={`passkey-delete-${pk.id}`}
                                    onClick={() => handleDelete(pk.id)}
                                    className="p-2 rounded-md text-slate-gray hover:text-red-600 hover:bg-red-50 transition-all"
                                    aria-label="Remove passkey"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                <button
                    data-testid="settings-security-add-btn"
                    onClick={handleAdd}
                    disabled={adding || !supported}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-95 disabled:opacity-60 transition-all"
                >
                    <Plus className="w-4 h-4" />
                    {adding ? "Follow the prompt…" : "Add a passkey"}
                </button>
            </section>
        </div>
    );
}
